import React, { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import PortalLayout from './PortalLayout';
import { useAuth } from '../../context/AuthContext';
import {
  Trophy, Award, ChevronLeft, MapPin, Plane, Edit, Loader2, Globe2, CalendarDays, Sparkles, MessageCircle,
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

/**
 * Member public profile (iter 73 cream restyle).
 *
 * Shows: name + photo, "Where I'm from", "Currently in", "Moving to",
 * member-since date, bio, points/rank, lesson progress, recent posts.
 * Owner sees their own referral count + an "Edit" CTA. No black blocks.
 */
const InitialBubble = ({ name, size = 96 }) => {
  const initial = (name || '?').trim()[0]?.toUpperCase() || '?';
  return (
    <div
      className="rounded-full bg-[#2CA8A0] text-white font-semibold flex items-center justify-center shrink-0"
      style={{ width: size, height: size, fontSize: size / 2.5 }}
      data-testid="member-photo-initial"
    >
      {initial}
    </div>
  );
};

const MetaItem = ({ icon: Icon, label, value, testId }) => {
  if (!value) return null;
  return (
    <div className="flex items-start gap-2.5" data-testid={testId}>
      <Icon className="w-4 h-4 text-[#1A5C6E] mt-0.5 shrink-0" />
      <div className="text-sm">
        <p className="font-mono uppercase text-[10px] tracking-[0.15em] text-[#0A1F2E]/55">{label}</p>
        <p className="text-[#0A1F2E]">{value}</p>
      </div>
    </div>
  );
};

const PortalMemberProfile = () => {
  const { userId } = useParams();
  const { user: authUser } = useAuth();
  const navigate = useNavigate();

  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showSaved, setShowSaved] = useState(false);

  useEffect(() => {
    if (sessionStorage.getItem('frg_profile_just_saved') === 'true') {
      setShowSaved(true);
      sessionStorage.removeItem('frg_profile_just_saved');
      const t = setTimeout(() => setShowSaved(false), 5000);
      return () => clearTimeout(t);
    }
  }, []);

  useEffect(() => {
    const fetchProfile = async () => {
      setLoading(true);
      try {
        const token = localStorage.getItem('auth_token');
        const res = await fetch(`${API_URL}/api/members/${userId}`, {
          headers: token ? { Authorization: `Bearer ${token}` } : {},
          credentials: 'include',
        });
        if (!res.ok) throw new Error((await res.json()).detail || 'Profile not found');
        setProfile(await res.json());
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };
    if (userId) fetchProfile();
  }, [userId]);

  if (loading) {
    return (
      <PortalLayout activeTab="profile">
        <div className="flex items-center justify-center min-h-[60vh]">
          <Loader2 className="w-8 h-8 text-[#2CA8A0] animate-spin" />
        </div>
      </PortalLayout>
    );
  }

  if (error || !profile) {
    return (
      <PortalLayout activeTab="profile">
        <div className="max-w-3xl mx-auto p-6 lg:p-8">
          <div className="bg-[#B91C1C]/10 border border-[#B91C1C]/30 text-[#B91C1C] p-4 rounded-xl text-sm" data-testid="member-profile-error">
            {error || 'Profile not found'}
          </div>
        </div>
      </PortalLayout>
    );
  }

  const displayName = `${profile.first_name}${profile.last_name_initial ? ` ${profile.last_name_initial}.` : ''}`;
  const fromLine = [profile.from_city, profile.from_country].filter(Boolean).join(', ');
  const memberSince = profile.member_since
    ? new Date(profile.member_since).toLocaleDateString('en-US', { month: 'long', year: 'numeric', timeZone: 'America/Chicago'})
    : null;

  return (
    <PortalLayout activeTab="profile">
      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-12 py-10 md:py-12" data-testid="member-profile">
        <button
          onClick={() => navigate(-1)}
          className="inline-flex items-center gap-1 text-[#1A5C6E] hover:text-[#0A1F2E] text-sm mb-6 transition font-mono uppercase tracking-[0.12em] text-[11px]"
          data-testid="member-profile-back"
        >
          <ChevronLeft className="w-4 h-4" /> Back
        </button>

        {showSaved && (
          <div
            className="bg-[#2CA8A0]/10 border border-[#2CA8A0]/40 text-[#1A5C6E] p-3 mb-6 rounded-xl text-sm"
            data-testid="profile-saved-banner"
          >
            Profile updated.
          </div>
        )}

        {/* Header card */}
        <section className="bg-white border border-[#EFE6CF] rounded-2xl p-6 sm:p-7" data-testid="member-header-card">
          <div className="flex flex-col min-[480px]:flex-row items-center min-[480px]:items-start gap-5 text-center min-[480px]:text-left">
            {profile.profile_photo_url ? (
              <img
                src={profile.profile_photo_url}
                alt={displayName}
                className="rounded-full object-cover shrink-0 ring-2 ring-[#EFE6CF]"
                style={{ width: 96, height: 96 }}
                data-testid="member-photo"
              />
            ) : (
              <InitialBubble name={profile.first_name} size={96} />
            )}

            <div className="flex-1 min-w-0 w-full">
              <div className="flex flex-col min-[480px]:flex-row items-center min-[480px]:items-start justify-between gap-3">
                <h1
                  className="font-display italic text-3xl md:text-4xl text-[#0A1F2E] break-words leading-tight"
                  data-testid="member-display-name"
                >
                  {displayName}
                </h1>
                {profile.is_self && (
                  <Link
                    to="/portal/profile/edit"
                    className="inline-flex items-center gap-1.5 font-mono uppercase text-[10px] tracking-[0.15em] bg-[#0A1F2E] hover:bg-[#163448] text-white px-4 py-2 rounded-full transition shrink-0"
                    data-testid="edit-profile-link"
                  >
                    <Edit className="w-3.5 h-3.5" /> Edit
                  </Link>
                )}
                {!profile.is_self && profile.allow_direct_messages !== false && (
                  <Link
                    to={`/portal/peer-circles/inbox?to=${profile.user_id}`}
                    className="inline-flex items-center gap-1.5 font-mono uppercase text-[10px] tracking-[0.15em] bg-[#2CA8A0] hover:bg-[#1A5C6E] text-white px-4 py-2 rounded-full transition shrink-0"
                    data-testid="member-message-btn"
                  >
                    <MessageCircle className="w-3.5 h-3.5" /> Message
                  </Link>
                )}
              </div>

              {profile.bio && (
                <p
                  className="text-[#0A1F2E]/75 text-sm mt-3 leading-relaxed break-words"
                  data-testid="member-bio"
                >
                  {profile.bio}
                </p>
              )}
            </div>
          </div>

          {/* Meta row */}
          {(fromLine || profile.current_city || profile.destination_city || memberSince) && (
            <div className="mt-6 pt-6 border-t border-[#EFE6CF] grid grid-cols-1 sm:grid-cols-2 gap-5">
              <MetaItem icon={Globe2} label="Where I'm from" value={fromLine} testId="member-from" />
              <MetaItem icon={MapPin} label="Currently in" value={profile.current_city} testId="member-current-city" />
              <MetaItem icon={Plane} label="Moving to" value={profile.destination_city} testId="member-destination-city" />
              <MetaItem icon={CalendarDays} label="Member since" value={memberSince} testId="member-since" />
            </div>
          )}
        </section>

        {/* Owner-only referral count */}
        {profile.is_self && profile.referral_count !== null && profile.referral_count !== undefined && (
          <Link
            to="/portal/peer-circles/invite"
            className="mt-5 bg-white border border-[#EFE6CF] hover:border-[#2CA8A0]/50 rounded-2xl p-5 flex items-center gap-4 transition"
            data-testid="member-referral-stat"
          >
            <div className="w-12 h-12 rounded-full bg-[#F4ECD8] flex items-center justify-center shrink-0">
              <Sparkles className="w-5 h-5 text-[#1A5C6E]" />
            </div>
            <div className="flex-1">
              <p className="font-display italic text-2xl text-[#0A1F2E] leading-none">
                {profile.referral_count}
              </p>
              <p className="text-[#0A1F2E]/65 text-sm mt-1">
                friends invited so far · tap to grab your invite link
              </p>
            </div>
          </Link>
        )}

        {/* Points + rank */}
        <section
          className="mt-5 bg-white border border-[#EFE6CF] rounded-2xl p-5 flex items-center gap-4"
          data-testid="member-points-badge"
        >
          <div className="w-12 h-12 rounded-full bg-[#F4ECD8] flex items-center justify-center shrink-0">
            <Trophy className="w-5 h-5 text-[#1A5C6E]" />
          </div>
          <div>
            <p className="font-display italic text-2xl text-[#0A1F2E] leading-none">
              {profile.points} <span className="font-body not-italic text-sm text-[#0A1F2E]/60">points</span>
            </p>
            <p className="text-[#0A1F2E]/65 text-xs flex items-center gap-1 mt-1">
              <Award className="w-3.5 h-3.5" /> Rank #{profile.rank} of {profile.rank_total}
            </p>
          </div>
        </section>

        {/* Lesson progress */}
        {profile.lessons_completed !== null && profile.lessons_completed !== undefined && (
          <section className="mt-5 bg-white border border-[#EFE6CF] rounded-2xl p-5" data-testid="member-progress-section">
            <p className="font-mono uppercase text-[10px] tracking-[0.15em] text-[#0A1F2E]/55">Lesson progress</p>
            <p className="text-[#0A1F2E] text-sm mt-1">
              <span className="font-display italic text-2xl text-[#1A5C6E]">{profile.lessons_completed}</span>
              <span className="ml-2 text-[#0A1F2E]/65">total lessons completed</span>
            </p>
          </section>
        )}

        {/* Posts */}
        {profile.posts !== null && profile.posts !== undefined && (
          <section className="mt-5 bg-white border border-[#EFE6CF] rounded-2xl p-5" data-testid="member-posts-section">
            <p className="font-mono uppercase text-[10px] tracking-[0.15em] text-[#0A1F2E]/55 mb-3">Posts</p>
            {profile.posts.length === 0 ? (
              <p className="text-[#0A1F2E]/60 text-sm">No posts yet</p>
            ) : (
              <ul className="space-y-3">
                {profile.posts.map((p) => (
                  <li key={p.post_id || p.id} className="text-[#0A1F2E] text-sm border-l-2 border-[#2CA8A0]/40 pl-3">
                    <p className="leading-snug">{p.body || p.content}</p>
                    <p className="text-[#0A1F2E]/50 text-xs mt-1">
                      {p.created_at ? new Date(p.created_at).toLocaleString('en-US', { timeZone: 'America/Chicago' }) : ''}
                    </p>
                  </li>
                ))}
              </ul>
            )}
          </section>
        )}
      </div>
    </PortalLayout>
  );
};

export default PortalMemberProfile;
